// 레이드 1단계(솔로 + AI 용병, docs/RAID-PRD.md): 잿불 비룡 패턴·용병·위협·보상 규칙. 데이터와 순수 함수만(DOM·저장소 없음).
// 레이드 정의(이름·해금·권장 Lv·주 3회·보상 표)는 items.ts RAIDS가 정본이고, 여기는 그 레이드를 실제 판으로 만드는 수치다.
// HP·피해는 tools/sim/raid.ts가 정하고 raid.test.ts가 지킨다(권장 영웅 + 용병 3이 180~240초, 패턴 한 대 = 권장 영웅 최대 HP의 12~20%).
import { HERO } from '../config';
import { LEVEL_STATS } from '../growth';
import { RAIDS } from './items';
import type { RaidDef } from './types';

export type RaidId = 'raid_wyvern';
/** 1단계에서 플레이할 수 있는 레이드(나머지 RAIDS는 보스·무대가 생기면 늘린다) */
export const PLAYABLE_RAIDS: readonly RaidId[] = ['raid_wyvern'];
export function isRaidId(v: unknown): v is RaidId { return typeof v === 'string' && (PLAYABLE_RAIDS as readonly string[]).includes(v); }
export function raidDef(id: RaidId): RaidDef { return RAIDS.find((r) => r.id === id)!; }
/** 판 열쇠(loadMap 스테이지 인자·결과 화면 다시 도전): 노말 = 레이드 id, 하드 = '<id>.hard' */
export function raidStageKey(raid: RaidId, diff: 'normal' | 'hard'): RaidId | `${RaidId}.hard` { return diff === 'hard' ? `${raid}.hard` : raid; }
/** 판 열쇠 → 레이드·난이도(레이드 열쇠가 아니면 null) */
export function parseRaidStage(key: string): { raid: RaidId; diff: 'normal' | 'hard' } | null {
  if (isRaidId(key)) return { raid: key, diff: 'normal' };
  const m = /^([a-z_]+)\.hard$/.exec(key);
  return m && isRaidId(m[1]) ? { raid: m[1], diff: 'hard' } : null;
}

// ── 용병 ─────────────────────────────────────────────
export type MercRole = 'tank' | 'archer' | 'priest';
export interface MercDef {
  readonly role: MercRole;
  readonly name: string;
  /** 역할 이름(파티 칸) */
  readonly title: string;
  /**
   * 영웅 기본 HP·공격(레벨만, 장비·트리 없음)에 곱한다. 권장 레벨에서 용병 셋의 DPS가 기대 영웅 보스 DPS의 약 40%(방패 12·궁수 22·사제 6),
   * 방패병 외 HP가 기대 영웅 최대 HP와 비슷하게 맞춘 값(tools/sim/raid.ts). 레벨이 오를수록 영웅 몫이 커진다
   */
  readonly hpMult: number;
  /** 영웅 기본 공격(레벨만)에 곱한다 */
  readonly atkMult: number;
  /** 공격 한 번 배율과 간격(틱), 사거리(m) */
  readonly hitMult: number;
  readonly hitEvery: number;
  readonly reach: number;
  /** 보스와 유지할 거리(m): 이 범위 안에서는 서서 공격한다 */
  readonly keepMin: number;
  readonly keepMax: number;
  /** 이동 속도(m/s) */
  readonly speed: number;
  /** 받는 피해 배율 */
  readonly damageTaken: number;
  /** 준 피해당 위협 배율 */
  readonly threat: number;
  /** 경고 반응 지연(틱, 최소~최대) */
  readonly react: readonly [number, number];
  /** 몸 반경(m) */
  readonly radius: number;
  /** 렌더: 모델(카엘·라이라 복제)과 색조 */
  readonly model: 'kael' | 'lyra';
  readonly tint: number;
}

export const MERCS: readonly MercDef[] = [
  { role: 'tank', name: '가렌', title: '방패병', hpMult: 4.5, atkMult: 3.2, hitMult: 1.0, hitEvery: 78, reach: 3.2, keepMin: 1.2, keepMax: 2.6, speed: 4.6, damageTaken: 0.65, threat: 3, react: [14, 26], radius: 0.5, model: 'kael', tint: 0x9cc4ff },
  { role: 'archer', name: '이브', title: '궁수', hpMult: 1.7, atkMult: 6.2, hitMult: 1.15, hitEvery: 96, reach: 13, keepMin: 8.5, keepMax: 12, speed: 5.0, damageTaken: 1, threat: 1, react: [12, 24], radius: 0.45, model: 'lyra', tint: 0x9fe08a },
  { role: 'priest', name: '루멘', title: '사제', hpMult: 1.8, atkMult: 3.8, hitMult: 0.8, hitEvery: 150, reach: 9, keepMin: 5.5, keepMax: 8, speed: 4.8, damageTaken: 1, threat: 1, react: [14, 26], radius: 0.45, model: 'kael', tint: 0xfff0b8 },
];
export const MERC_COUNT = MERCS.length;

/** 방패병 도발: 간격·길이(틱) */
export const TAUNT = { every: 14 * 60, ticks: 6 * 60 } as const;
/** 사제 치유: 간격(틱)·최대 HP 비율·사거리(m, 무대 반지름 정도 — 보스 옆 영웅까지 닿게)·치유량 위협 배율 */
export const HEAL = { every: 4 * 60, ratio: 0.12, range: 18, threat: 0.5 } as const;
/** 용병 쓰러짐 → 다시 일어나기(틱)·그때 HP 비율 */
export const MERC_REVIVE = { ticks: 15 * 60, hpRatio: 0.5 } as const;

/** 레벨만으로 정한 영웅 기본 능력치(장비·트리 없음) — 용병 수치의 기준 */
export function levelBase(level: number): { attack: number; maxHp: number } {
  const lv = Math.max(1, Math.floor(level));
  return { attack: HERO.attack + (lv - 1) * LEVEL_STATS.attack, maxHp: HERO.maxHp + (lv - 1) * LEVEL_STATS.maxHp };
}
export function mercStats(def: MercDef, level: number): { attack: number; maxHp: number } {
  const b = levelBase(level);
  return { attack: Math.max(1, Math.round(b.attack * def.atkMult)), maxHp: Math.max(1, Math.round(b.maxHp * def.hpMult)) };
}

// ── 비룡 ─────────────────────────────────────────────
/**
 * 잿불 비룡 수치. 시간은 틱(60Hz), 거리는 m. dmg = 패턴 배율(× WYVERN.damage). 경고 = 판정(경고 모양 그대로 맞는다).
 * hp·damage는 tools/sim/raid.ts가 정한 값(raid.test.ts가 시뮬과 맞는지 본다).
 */
export const WYVERN = {
  name: '잿불 비룡',
  /** 몸 반경(판정·충돌), 걷기 속도 */
  radius: 2.6,
  speed: 2.4,
  hp: 232000,
  /** 패턴 피해 1.0의 값(받는 피해 배율 전) */
  damage: 188,
  /** 등장(문 닫힘 뒤 내려앉기) */
  intro: 150,
  /** 공격 사이 쉬는 틱 */
  cooldown: 54,
  /** 대상 다시 고르기 간격·바꾸는 위협 차이 */
  retargetEvery: 180,
  retargetMargin: 1.1,
  bite: { windup: 60, range: 6, arcDeg: 70, dmg: 1.0, reach: 5.5 },
  tail: { windup: 66, range: 7, arcDeg: 120, dmg: 0.9, push: 3 },
  pools: { windup: 84, radius: 2.4, dmg: 0.8, extraP2: 2, scatter: 9 },
  /** 불 장판: 남는 시간·피해 간격·한 번 피해(× damage) */
  fire: { ticks: 330, every: 30, dmg: 0.12, radius: 2.4, max: 12 },
  charge: { windup: 72, length: 15, width: 3, dmg: 1.1, dashTicks: 20 },
  /** 페이즈 2 비행 순환 */
  flight: { phase: 0.5, firstDelay: 30, every: 25 * 60, takeoff: 180, height: 7, breaths: 2, breathWindup: 96, breathGap: 54, breathWidth: 6, breathLength: 42, breathDmg: 1.3, landWindup: 84, landRadius: 5, landDmg: 1.2, groundAttacks: 4 },
  /** 광폭: 입장 뒤 틱, 쉬는 시간 배율, 피해 배율 */
  enrage: { at: 6 * 3600, cooldownMult: 0.7, dmgMult: 1.5 },
  /** 경직: 최대 HP 비율 누적마다 2초 */
  staggerRatio: 0.12,
  staggerTicks: 120,
  dying: 150,
} as const;

/** 보스 패턴 번호(Ev.BossPattern a, b = BossKind.Wyvern). 렌더·소리·HUD가 읽는다 */
export const WyvernPattern = { bite: 0, tail: 1, pools: 2, charge: 3, takeoff: 4, breath: 5, land: 6, phase2: 9, enrage: 10, brand: 11 } as const;

// ── 난이도(C13 레이드 하드, docs/RAID-HARD.md) ──────────────
/** 레이드 난이도. 하드 = 1차 환생 뒤(PROGRESSION-720H 6절 레이드 난이도). 악몽은 다음 단계 */
export type RaidDiff = 'normal' | 'hard';
export const RAID_DIFFS: readonly RaidDiff[] = ['normal', 'hard'];
export function isRaidDiff(v: unknown): v is RaidDiff { return v === 'normal' || v === 'hard'; }

/**
 * 하드 판 수치(레이드마다). 보스 HP·피해는 노말 값을 덮는다(배율이 아니라 시뮬 값 — tools/sim/raid.ts raidSimAt(recLevel)).
 * 권장 Lv60 영웅(지역5 장비 +9) + 용병 3 정상 입력 봇 실측 기준(raid.test RAID_HARD_BOT): 처치 4~6분(노말 봇 약 4.3분보다 길다), 패턴 한 대가 노말보다 아프다.
 * brand = 하드 전용 기믹 「잿불 낙인」(로스트아크 군단장 하드의 산개 기믹, 디아블로 4 우버 보스의 낙인 폭발): 지상 공격 every번마다
 * 파티원 둘(지금 대상 + 무작위 한 명)의 발밑에 따라다니는 원을 찍고 windup 뒤 폭발한다. 낙인 주인은 bearerDmg, 원 안에 같이 선 다른 파티원은
 * crowdDmg(크다 — 겹쳐 서면 벌을 받는다). 터진 자리에는 불 장판이 남는다(무대가 점점 좁아진다)
 */
export interface RaidHardDef {
  readonly recLevel: number;
  readonly hp: number;
  readonly damage: number;
  readonly enrageAt: number;
  readonly cooldownMult: number;
  /** 페이즈 1부터 화염 장판에 더하는 무작위 원 수(노말은 페이즈 2에서만 extraP2) */
  readonly poolsExtraP1: number;
  readonly brand: { readonly every: number; readonly marks: number; readonly radius: number; readonly windup: number; readonly bearerDmg: number; readonly crowdDmg: number };
  /** 보상 배율(골드·강화석 개수)·전설 확률 덮어쓰기·세트 부위 아이템 레벨 */
  readonly loot: { readonly goldMult: number; readonly stonesMult: number; readonly legendChance: number; readonly ilvl: number };
  /** 하드 첫 클리어 보상(고정 영수증, 한 번): 골드·강화석·확정 전설·칭호 */
  readonly first: { readonly gold: number; readonly stones: number; readonly title: string };
}

/** 하드 판이 있는 레이드(보스·무대가 플레이 가능한 레이드만 — 서리 거인·모래 군주·심연의 용은 보스가 생기면 이 표에 더한다) */
export const RAID_HARD: Readonly<Partial<Record<RaidId, RaidHardDef>>> = {
  raid_wyvern: {
    recLevel: 60, hp: 900000, damage: 600, enrageAt: 7 * 3600, cooldownMult: 0.85, poolsExtraP1: 1,
    brand: { every: 5, marks: 2, radius: 3.4, windup: 240, bearerDmg: 0.8, crowdDmg: 1.6 },
    loot: { goldMult: 2.5, stonesMult: 1.5, legendChance: 0.12, ilvl: 60 },
    first: { gold: 40000, stones: 60, title: '잿불을 꺾은 자' },
  },
};
export function raidHardDef(id: RaidId): RaidHardDef | null { return RAID_HARD[id] ?? null; }
export function hasHard(id: RaidId): boolean { return !!RAID_HARD[id]; }
/** 하드 해금: 하드 판이 있고, 어느 영웅이든 1차 환생을 했다(maxCycle ≥ 1) */
export function hardUnlocked(id: RaidId, maxCycle: number): boolean { return hasHard(id) && maxCycle >= 1; }

/** 판 수치(난이도 반영): 보스 HP·패턴 피해 1.0·광폭 시각·쉬는 시간 배율 */
export interface RaidTuning { readonly hp: number; readonly damage: number; readonly enrageAt: number; readonly cooldownMult: number; readonly poolsExtraP1: number; readonly brand: RaidHardDef['brand'] | null }
export function raidTuning(id: RaidId, diff: RaidDiff): RaidTuning {
  const h = diff === 'hard' ? raidHardDef(id) : null;
  if (!h) return { hp: WYVERN.hp, damage: WYVERN.damage, enrageAt: WYVERN.enrage.at, cooldownMult: 1, poolsExtraP1: 0, brand: null };
  return { hp: h.hp, damage: h.damage, enrageAt: h.enrageAt, cooldownMult: h.cooldownMult, poolsExtraP1: h.poolsExtraP1, brand: h.brand };
}

// ── 위협 ─────────────────────────────────────────────
/** 파티 칸 번호: 0 = 영웅, 1..3 = 용병(MERCS 순서) */
export const PARTY_SIZE = 1 + MERC_COUNT;
/**
 * 다음 대상: 도발 중이면 방패병, 아니면 위협이 가장 높은 살아 있는 칸. 지금 대상이 살아 있으면 margin 배를 넘을 때만 바꾼다.
 * alive[i] = 살아 있는지, threat[i] = 누적 위협. 아무도 없으면 -1
 */
export function pickTarget(current: number, alive: ArrayLike<boolean>, threat: ArrayLike<number>, tauntBy: number, margin: number): number {
  if (tauntBy >= 0 && alive[tauntBy]) return tauntBy;
  let best = -1, bestT = -1;
  for (let i = 0; i < alive.length; i++) if (alive[i] && threat[i] > bestT) { best = i; bestT = threat[i]; }
  if (best < 0) return -1;
  if (current >= 0 && current < alive.length && alive[current] && threat[best] <= threat[current] * margin) return current;
  return best;
}

// ── 주간 보상 ───────────────────────────────────────
/**
 * 주 보상 영수증 id(save.v1 보너스 영수증): 이번 주 n번째(1..weeklyLimit) 클리어. 노말 = 'raid:<id>:<주>:<n>'(예전 그대로),
 * 하드 = 'raid:<id>.hard:<주>:<n>'. 주 보상 횟수는 난이도를 합쳐 센다(로스트아크 군단장처럼 레이드당 주 3회 — n 자리를 노말·하드가 나눠 쓴다).
 * 옛 빌드의 정규식([a-z_]+)은 '.hard'를 읽지 못해 하드 영수증을 건너뛴다(롤백 안전: 정산 표시는 raid.v1에 남는다)
 */
export function raidReceiptId(raid: RaidId, week: number, n: number, diff: RaidDiff = 'normal'): string { return `raid:${raid}${diff === 'hard' ? '.hard' : ''}:${week}:${n}`; }
/** 하드 첫 클리어 영수증 id(한 번 — 칭호·확정 전설). 주와 상관없다 */
export function raidFirstId(raid: RaidId, diff: RaidDiff): string { return `raid:${raid}.${diff}:first`; }
/**
 * 영수증 id → 레이드·난이도·주·n(첫 클리어면 first=true, week=n=0). 레이드 영수증이 아니면 null.
 * 정준형만 받는다: 'raid:raid_wyvern:042:01' 같은 앞 0·'-0' 꼴은 다시 만든 id(raidReceiptId·raidFirstId)와 달라 null —
 * 안 그러면 같은 영수증이 문자열만 달라 서버 장부(run_id 유일)를 여러 줄로 통과한다
 */
export function parseRaidReceipt(id: string): { raid: RaidId; diff: RaidDiff; week: number; n: number; first: boolean } | null {
  const f = /^raid:([a-z_]+)\.(hard):first$/.exec(id);
  if (f) return isRaidId(f[1]) && hasHard(f[1]) && raidFirstId(f[1], 'hard') === id ? { raid: f[1], diff: 'hard', week: 0, n: 0, first: true } : null;
  const m = /^raid:([a-z_]+)(\.hard)?:(-?\d+):(\d+)$/.exec(id);
  if (!m || !isRaidId(m[1])) return null;
  const diff: RaidDiff = m[2] ? 'hard' : 'normal';
  if (diff === 'hard' && !hasHard(m[1])) return null;
  const week = Number(m[3]), n = Number(m[4]);
  if (!Number.isSafeInteger(week) || n < 1 || n > raidDef(m[1]).weeklyLimit) return null;
  return raidReceiptId(m[1], week, n, diff) === id ? { raid: m[1], diff, week, n, first: false } : null;
}
/** 보상 장부 연산 id(장비·강화석·주문서): 영수증 id + 항목 번호 */
export function raidOpId(raid: RaidId, week: number, n: number, k: string, diff: RaidDiff = 'normal'): string { return `${raidReceiptId(raid, week, n, diff)}:${k}`; }
/**
 * 이번 주 로컬 영수증이 찬 n 자리 수(n 자리에 노말·하드 어느 영수증이든 있으면 찬 것). C1a부터 서버 장부와 합쳐 셀 때 n이 1부터 차례가 아닐 수 있어
 * (예: 넷 판 2번 뒤 솔로 = n 3) 차례로 세지 않고 찬 자리를 모두 센다. 게임 정산은 game/raidLedger.ts raidUnifiedDone(영수증 수 ∪ 서버 장부)을 쓴다
 */
export function raidWeekClears(has: (id: string) => boolean, raid: RaidId, week: number): number {
  const limit = raidDef(raid).weeklyLimit;
  let n = 0;
  for (let i = 1; i <= limit; i++) if (has(raidReceiptId(raid, week, i)) || has(raidReceiptId(raid, week, i, 'hard'))) n++;
  return n;
}
/** 이번 주 남은 보상 횟수 */
export function raidWeekLeft(has: (id: string) => boolean, raid: RaidId, week: number): number {
  return raidDef(raid).weeklyLimit - raidWeekClears(has, raid, week);
}
/** 레이드 클리어 EXP 배율(지역2 보스 처치 EXP ×) */
export const RAID_XP_MULT = 3;

/** 문자열 씨앗 난수(FNV-1a → mulberry32). 영수증 id로 보상을 굴려 새로고침으로 다시 굴릴 수 없게 한다 */
export function seededRand(seed: string): () => number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
